import { pick } from 'lodash';

export interface IdlList{
  name: string;
  content: string;
  isMain: boolean;
}
export interface EnvList{
  environmentId: string;
  configId: string;
  name: string;
  serviceId: string;
  auditId?: string;
  auditStatus?: AuditStatusEnum;
  auditMessage?: string;
  auditStates?: {
    canRelease: boolean;
    canCancel: boolean;
  };
}
export interface EnvironmentListRes{
  code: number;
  msg: string;
  envList: EnvList[];
}

export interface IdlListRes{
  code: number;
  msg: string;
  envList: IdlList[];
}

export enum AuditProcessStatusEnum {
  Timeout = -1,
  Pending,
  Auditing,
  Audited,
}

export enum AuditReviewStatusEnum {
  Timeout = -2,
  Cancel,
  Reject,
  Approve,
}

export interface AuditHistoryProps {
  progressStatus: AuditProcessStatusEnum;
  title: string;
  operator: {
    nickName: string;
    operateTime: string;
    staffAvatar: string;
    reviewStatus: AuditReviewStatusEnum;
  }[];
}

export interface AuditInfoProps {
  canAudit: boolean;
  canRelease: boolean;
  history: AuditHistoryProps[];
}

export interface InterfaceItem{
  configId: string;
  serviceId: string;
  environmentId: string;
  path: string;
  method: string;
  active: boolean;
  title: string;
  status?: {
    enumExpired: boolean;
  };
}

export interface DiffInterfaceItem{
  path: string;
  type: string;
  method: string;
  newConfig: any;
  oldConfig: any;
}

export interface InterfaceListRes{
  code: number;
  msg: string;
  interfaceList: InterfaceItem[];
}

export interface InterfaceDetailInfo{
  interfaceId: number;
  url: string;
  method: string;
  apiConfig: any;
  routeConfig: any;
}

export interface InterfaceDetailRes{
  code: number;
  msg: string;
  configList: InterfaceDetailInfo;
}

/**
 * InterfaceDetail —— APIConfig —— 转换插件
 */
export interface TransPluginItem {
  pluginId?: number; // 插件ID（全局唯一）新建的插件没有pluginId
  name: string; // 插件名（全局唯一）
  content: string; // 代码
  requestParams: string[]; // 请求参数
  resultParams: string[]; // 返回参数
  description: string; // 描述
  staffName?: string; // 最近一次修改操作人
}

/**
 * InterfaceDetail —— APIConfig —— 转换插件(table展示部分，从new_read接口中获取)
 */
export interface TransPluginTableItem extends Omit<TransPluginItem, 'pluginId' | 'content' | 'staffName'> {
  type: 2;
}

/**
 * InterfaceDetail —— APIConfig —— 转换插件(插件接口返回部分，api/custom_plugin/get中获取)
 */
export type TransPluginItemContent = Pick<TransPluginItem, 'pluginId' | 'name' | 'content' | 'staffName'>;

/**
 * InterfaceDetail —— APIConfig —— 转换插件(更新插件部分，/gateway/api/interface/new_update/中的custom_plugin_list)
 */
export type TransPluginUpdateItem = Pick<TransPluginItem, 'name' | 'content'>;

/**
 * TransPluginItem的初始化值构造器
 */
export const initTransPluginItemCreator = (): TransPluginItem => ({
  pluginId: -1,
  name: '',
  content: '',
  requestParams: [],
  resultParams: [],
  description: '',
  staffName: '',
});

/**
 * 将完整的 TransPluginItem 拆分为表单提交的item 和 插件更新的item
 * @param transPluginItem 需要拆分的完整的TransPluginItem
 */
export const splitPluginItem = (transPluginItem: TransPluginItem): [TransPluginTableItem, Pick<TransPluginItem, 'name' | 'content'>] => {
  const tableItem: TransPluginTableItem = {
    ...pick(transPluginItem, ['name', 'requestParams', 'resultParams', 'description']),
    type: 2,
  };
  const contentItem: TransPluginUpdateItem = pick(transPluginItem, ['name', 'content']);
  return [tableItem, contentItem];
};

/**
 * 公共插件(旧)
 */
export interface OldPluginItem {
  name: string;
  params: {
    requestParams?: string[];
    resultParams?: string[];
  }[];
  type: 1;
  dst: {
    description: string;
  };
  src: {
    description: string;
  };
}
/**
 * 整合新旧插件类型
 */
export type CombinePluginItem = OldPluginItem | TransPluginTableItem;

/**
 * release/diff 接口中 custom_plugin_diff 字段下显示的插件diff信息
 */
export interface PluginDiff {
  type: string; // 类型 【update or new or delete】
  path: string; // 插件关联的接口
  plugins: { // 每个path的自定义插件列表
    oldConfig: PluginDiffConfig | null;
    newConfig: PluginDiffConfig | null;
  }[];
}

interface PluginDiffConfig {
  serviceId: string;
  environmentId: string;
  configId: string; // 插件id
  name: string;
  content: string;
}

/**
 * release/diff 接口中 custom_plugin_diff 字段下显示的插件diff信息
 */
export interface PluginDiffSubmitList {
  type: string; // 类型 【update or new or delete】
  configId: string; // 插件id
}

/**
 * 函数插件的函数库中的 运算符 item
 */
export interface PluginFuncLibItemOperator {
  type: 'operator';
  name: string;
  starlingKeyName: string; // 多语言key
  level: number;
  expression: string;
  description: string;
  starlingKeyDescription: string; // 多语言描述key
  example: string;
}

/**
 * 函数插件的函数库中的 函数 item
 */
export interface PluginFuncLibItemFunc {
  type: 'function';
  name: string;
  starlingKeyName: string; // 多语言key
  level: number;
  expression: string;
  description: string;
  starlingKeyDescription: string; // 多语言描述key
  example: string;
  params: {
    name: string;
    starlingKeyName: string; // 多语言key
    description: string;
    starlingKeyDescription: string; // 多语言描述key
    serialNum: number;
  }[];
}

/**
 * 函数插件的函数库
 */
export interface PluginFuncLib {
  name: string;
  starlingKeyName: string; // 多语言key
  type: string;
  level: number;
  children: {
    name: string;
    starlingKeyName: string; // 多语言key
    type: string;
    level: number;
    children: (PluginFuncLibItemOperator | PluginFuncLibItemFunc)[];
  }[];
}

/**
 * 常用函数转换插件的属性
 */
export type TransFuncPluginItem = {
  type: 'convert'; // convert 转化函数 / check 校验函数
  paramAttr: {
    src: string;
    type: string;
  }; // 对应参数的相关属性
  funcExpressions: string[]; // 函数表达式list
} | {
  type: 'check'; // convert 转化函数 / check 校验函数
  paramAttr: {
    src: string;
    type: string;
  }; // 对应参数的相关属性
  funcExpressions: {
    expr: string;
    errorMsg: string;
  }[]; // 函数表达式和错误信息list
};

/**
 * 常用函数转换插件的属性构造器
 */
export const initFuncPluginItemCreator = (type: 'convert' | 'check' = 'convert', paramAttr: { src: string; type: string } = { src: '', type: 'string' }): TransFuncPluginItem => (type === 'convert' ? {
  type,
  paramAttr,
  funcExpressions: [''],
} : {
  type,
  paramAttr,
  funcExpressions: [{
    expr: '',
    errorMsg: '',
  }],
});

/*
 * 审核状态，0:进行中，1:通过，-1:拒绝，-2:过期, -3: 取消,2: 已执行, 只有 0，1，-1，-2显示审核版本
 * 其中-1，-2的时候可以再次提交审核，0，1的时候不能提交审核
*/
export enum AuditStatusEnum {
  Cancel = -3,
  Expire,
  Rejected,
  Pending,
  Approved,
  Exceeded
}

export enum ConfigVersionTypeEnum {
  Develop = 'develop',
  Audit = 'audit',
  Master = 'master',
}

export interface ConditionItem{
  key: string;
  dst: string;
  target: string;
}
export interface PluginItem{
  name: string;
  src: string;
  origin: string;
  target: string;
  conditions: Array<ConditionItem>;
  deleteKey: string[];
  params: string[];
  key?: number;
}

export interface CreateLabelData{
  teamId: string;
  serviceId: string;
  environmentId: string;
  configId: string;
  sceneLabel?: string[]; // 可以忽略 直接全量更新
  sceneLabelList?: string[];
}

export interface DeleteLableData{
  teamId: string;
  serviceId: string;
  environmentId: string;
  interfaceId: string;
  sceneLabel: string[];
}

export interface GetLableQuery{
  teamId: string;
  serviceId: string;
  environmentId: string;
  interfaceId: string;
}
